import React, { useState, useEffect, useRef, memo } from 'react';
import SafeWidgetWrapper from './SafeWidgetWrapper';
import OptionsChartWidget from './OptionsChartWidget';
import { deleteWidget, updateWidget } from '../../services/api';
import CategoryAccordion from './CategoryAccordion';
import { formatIndicatorDisplay, mergeCustomIndicators } from '../../utils/indicatorCategories';
import IndicatorSettingsModal from './IndicatorSettingsModal';
import { useCustomIndicators } from '../../contexts/CustomIndicatorContext';
import { GripVertical, X, ChevronDown, Edit2, Lock, Unlock, TrendingUp } from 'lucide-react';
import SearchableTicker from '../SearchableTicker';
import Tooltip from '../Tooltip';

function WidgetCard({ widget, onDelete, onUpdate, isLinked, crosshairDate, onCrosshairMove }) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedIndicators, setEditedIndicators] = useState(widget?.config?.indicators || []);
  const [localWidget, setLocalWidget] = useState(widget);
  const [showBollinger, setShowBollinger] = useState(false);
  const [showIndicatorDropdown, setShowIndicatorDropdown] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  const [hoveredIndicator, setHoveredIndicator] = useState(null);
  const [isEditingTicker, setIsEditingTicker] = useState(false);
  const [selectedTickerForEdit, setSelectedTickerForEdit] = useState(null);
  const dropdownRef = useRef(null);

  // Get custom indicators
  const { customIndicators } = useCustomIndicators();
  const mergedCategories = mergeCustomIndicators(customIndicators);

  // Sync localWidget when widget prop changes
  useEffect(() => {
    setLocalWidget(widget);
  }, [widget]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowIndicatorDropdown(false);
      }
    };

    if (showIndicatorDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showIndicatorDropdown]);

  // Safety check for widget
  if (!widget) {
    return <div className="h-full bg-background border border-muted rounded p-4">Invalid widget</div>;
  }

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${widget.ticker} widget?`)) return;

    try {
      await deleteWidget(widget.id);
      onDelete();
    } catch (error) {
      console.error('Error deleting widget:', error);
      alert('Failed to delete widget');
    }
  };

  const handleDateRangeChange = async (e) => {
    const newRange = e.target.value;
    try {
      const updatedWidget = {
        ...localWidget,
        config: { ...(localWidget.config || {}), date_range: newRange }
      };

      const response = await updateWidget(localWidget.id, updatedWidget);

      // Update local state immediately
      setLocalWidget(response.data || updatedWidget);

      // Notify parent component
      if (onUpdate) {
        onUpdate(response.data || updatedWidget);
      }
    } catch (error) {
      console.error('Error updating date range:', error);
      alert('Failed to update date range');
    }
  };

  const handleUpdateWidget = async (widgetId, updatedData) => {
    try {
      const response = await updateWidget(widgetId, updatedData);

      // Update local state
      setLocalWidget(response.data || updatedData);

      // Notify parent
      if (onUpdate) {
        onUpdate(response.data || updatedData);
      }
    } catch (error) {
      console.error('Error updating widget:', error);
      throw error;
    }
  };

  const handleEditIndicators = () => {
    setIsEditMode(true);
    setEditedIndicators(localWidget?.config?.indicators || []);
  };

  const handleSaveIndicators = async () => {
    try {
      const updatedWidget = {
        ...localWidget,
        config: { ...(localWidget.config || {}), indicators: editedIndicators }
      };

      const response = await updateWidget(localWidget.id, updatedWidget);

      // Update local state
      setLocalWidget(response.data || updatedWidget);
      setIsEditMode(false);

      // Notify parent
      if (onUpdate) {
        onUpdate(response.data || updatedWidget);
      }
    } catch (error) {
      console.error('Error updating indicators:', error);
      alert('Failed to update indicators');
    }
  };

  const toggleIndicator = (indicator) => {
    if (editedIndicators.includes(indicator)) {
      setEditedIndicators(editedIndicators.filter(i => i !== indicator));
    } else {
      setEditedIndicators([...editedIndicators, indicator]);
    }
  };

  const handleOpenSettings = (indicator) => {
    setSelectedIndicator(indicator);
    setShowSettingsModal(true);
    setShowIndicatorDropdown(false); // Close dropdown when opening settings
  };

  const handleSaveSettings = async (indicator, settings) => {
    try {
      const currentSettings = localWidget.config?.indicator_settings || {};
      const newSettings = {
        ...currentSettings,
        [indicator]: settings
      };

      const updatedWidget = {
        ...localWidget,
        config: {
          ...(localWidget.config || {}),
          indicator_settings: newSettings
        }
      };

      const response = await updateWidget(localWidget.id, updatedWidget);

      // Update local state
      setLocalWidget(response.data || updatedWidget);

      // Notify parent
      if (onUpdate) {
        onUpdate(response.data || updatedWidget);
      }
    } catch (error) {
      console.error('Error saving indicator settings:', error);
      throw error;
    }
  };

  const handleToggleLock = async () => {
    const isDynamic = localWidget.use_dynamic_ticker;

    try {
      const updatedWidget = {
        ...localWidget,
        use_dynamic_ticker: !isDynamic  // Simple toggle
      };
      await handleUpdateWidget(localWidget.id, updatedWidget);
    } catch (error) {
      console.error('Error toggling lock:', error);
      alert('Failed to toggle lock');
    }
  };

  const handleTickerClick = () => {
    setIsEditingTicker(true);
    setSelectedTickerForEdit({ value: localWidget.ticker, label: localWidget.ticker });
  };

  const handleTickerChange = async (newTicker) => {
    if (!newTicker || !newTicker.value) {
      setIsEditingTicker(false);
      return;
    }

    try {
      const updatedWidget = {
        ...localWidget,
        ticker: newTicker.value.toUpperCase(),
        // Keep current lock state - don't force unlock
      };

      await handleUpdateWidget(localWidget.id, updatedWidget);
      setIsEditingTicker(false);
      setSelectedTickerForEdit(null);
    } catch (error) {
      console.error('Error updating ticker:', error);
      alert('Failed to update ticker');
    }
  };

  return (
    <div className="widget-card h-full bg-background border-2 border-gray-800 rounded overflow-hidden flex flex-col">
      <div className="bg-gray-900 px-3 py-3 flex justify-between items-center border-b border-gray-800">
        <div className="flex items-center gap-2">
          <GripVertical className="drag-handle w-4 h-4 text-gray-400 cursor-move" />

          {/* Lock/Unlock Icon with Tooltip */}
          <Tooltip
            content={
              <div>
                <div className="font-semibold mb-1">
                  {localWidget.use_dynamic_ticker ? "Unlocked (Dynamic)" : "Locked (Static)"}
                </div>
                <div className="text-gray-300 text-xs">
                  {localWidget.use_dynamic_ticker
                    ? "This widget updates when you change tickers. Click to lock it to the current ticker."
                    : "This widget always shows this ticker. Click to unlock and make it dynamic."
                  }
                </div>
              </div>
            }
            placement="bottom"
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleToggleLock();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              className={localWidget.use_dynamic_ticker
                ? "text-blue-400 hover:text-blue-300 transition-colors"
                : "text-gray-400 hover:text-white transition-colors"
              }
            >
              {localWidget.use_dynamic_ticker ? (
                <Unlock className="w-4 h-4" />
              ) : (
                <Lock className="w-4 h-4" />
              )}
            </button>
          </Tooltip>

          {/* Ticker Display or Edit */}
          {isEditingTicker ? (
            <div className="min-w-[150px]" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
              <SearchableTicker
                onChange={handleTickerChange}
                value={selectedTickerForEdit}
                placeholder="Search ticker..."
              />
            </div>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleTickerClick();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              className="drag-handle hover:bg-gray-700/50 px-2 py-1 rounded transition"
              title="Click to change ticker"
            >
              <span className={`font-semibold ${
                localWidget.use_dynamic_ticker ? 'text-blue-400' : 'text-text'
              }`}>
                {localWidget.ticker}
              </span>
            </button>
          )}

          {/* Options Chart Contract Info */}
          {localWidget.widget_type === 'options_chart' && (
            <div className="flex items-center gap-2 text-xs">
              <span className={localWidget.config?.option_type === 'call' ? 'text-green-400' : 'text-red-400'}>
                <TrendingUp className="w-3.5 h-3.5 inline mr-1" />
                {localWidget.config?.option_type?.toUpperCase()}
              </span>
              <span className="text-gray-400">${localWidget.config?.strike}</span>
              <span className="text-gray-500">{localWidget.config?.expiration}</span>
            </div>
          )}

          {!isEditMode ? (
            <>
              <select
                value={localWidget.config?.date_range || '1y'}
                onChange={handleDateRangeChange}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                className="bg-background text-text text-xs px-2 py-1 rounded border border-muted hover:border-primary transition"
              >
                <option value="1m">1M</option>
                <option value="3m">3M</option>
                <option value="6m">6M</option>
                <option value="1y">1Y</option>
                {localWidget.widget_type !== 'options_chart' && (
                  <>
                    <option value="2y">2Y</option>
                    <option value="5y">5Y</option>
                  </>
                )}
              </select>

              {/* Indicators Dropdown - Only for price charts */}
              {localWidget.widget_type !== 'options_chart' && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowIndicatorDropdown(!showIndicatorDropdown);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded transition flex items-center gap-1"
                  title="Indicators"
                >
                  <span>Indicators</span>
                  <ChevronDown className="w-3 h-3" />
                </button>

                {showIndicatorDropdown && (
                  <div
                    className="absolute top-full left-0 mt-1 bg-gray-800 border border-gray-700 rounded shadow-lg z-50 min-w-64 max-h-96 overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {/* Active Indicators (Robinhood style) */}
                    <div className="px-3 py-3 border-b border-gray-700">
                      <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Active Indicators</p>
                      <div className="space-y-1">
                        {localWidget?.config?.indicators?.length > 0 ? (
                          localWidget.config.indicators.map(ind => {
                            const settings = localWidget.config?.indicator_settings?.[ind] || {};
                            const displayText = formatIndicatorDisplay(ind, settings);

                            return (
                              <div
                                key={ind}
                                className="flex items-center justify-between group hover:bg-gray-700/50 px-2 py-1.5 rounded transition-colors cursor-pointer"
                                onMouseEnter={() => setHoveredIndicator(ind)}
                                onMouseLeave={() => setHoveredIndicator(null)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenSettings(ind);
                                }}
                              >
                                <span className="text-sm text-gray-200 flex-1">
                                  {displayText}
                                </span>
                                {hoveredIndicator === ind && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenSettings(ind);
                                    }}
                                    className="text-gray-400 hover:text-white transition-colors"
                                    title="Edit settings"
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <span className="text-sm text-gray-500">None</span>
                        )}
                      </div>
                    </div>

                    {/* Bollinger Bands Toggle (only if active) */}
                    {localWidget?.config?.indicators?.includes('bollinger') && (
                      <div className="px-3 py-2 border-b border-gray-700">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={showBollinger}
                            onChange={(e) => setShowBollinger(e.target.checked)}
                            className="w-3 h-3"
                          />
                          <span className="text-xs text-gray-300">Show Bollinger Bands</span>
                        </label>
                      </div>
                    )}

                    {/* Available Indicators with Accordion */}
                    <div className="px-3 py-2 border-b border-gray-700">
                      <p className="text-xs text-gray-400 mb-2">Quick Select:</p>
                      <CategoryAccordion
                        categories={mergedCategories}
                        selectedItems={localWidget?.config?.indicators || []}
                        onEdit={(indicator) => {
                          handleOpenSettings(indicator);
                        }}
                        onToggle={(indicator) => {
                          const currentIndicators = localWidget?.config?.indicators || [];

                          // Check if this is a custom indicator
                          const isCustom = indicator.startsWith('custom_');
                          let baseIndicator = indicator;
                          let customSettings = null;

                          if (isCustom) {
                            // Extract custom indicator ID and find its settings
                            const customId = parseInt(indicator.replace('custom_', ''));
                            const customIndicator = customIndicators.find(ci => ci.id === customId);

                            if (customIndicator) {
                              baseIndicator = customIndicator.base_indicator;
                              customSettings = customIndicator.settings;
                            }
                          }

                          // Toggle indicator in the list
                          const newIndicators = currentIndicators.includes(baseIndicator)
                            ? currentIndicators.filter(i => i !== baseIndicator)
                            : [...currentIndicators, baseIndicator];

                          // Build updated config
                          const updatedConfig = {
                            ...(localWidget.config || {}),
                            indicators: newIndicators
                          };

                          // If adding a custom indicator, apply its custom settings
                          if (isCustom && customSettings && !currentIndicators.includes(baseIndicator)) {
                            updatedConfig.indicator_settings = {
                              ...(localWidget.config?.indicator_settings || {}),
                              [baseIndicator]: customSettings
                            };
                          }

                          handleUpdateWidget(localWidget.id, {
                            ...localWidget,
                            config: updatedConfig
                          }).then(() => {
                            // Close dropdown after selection
                            setShowIndicatorDropdown(false);
                          }).catch(err => {
                            console.error('Failed to update indicator:', err);
                          });
                        }}
                        displayMode="compact"
                      />
                    </div>

                    {/* Edit indicators button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowIndicatorDropdown(false);
                        handleEditIndicators();
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      className="w-full px-3 py-2 text-xs text-left hover:bg-gray-700 transition text-gray-300"
                    >
                      ⚙️ Edit Mode (Advanced)
                    </button>
                  </div>
                )}
              </div>
              )}
            </>
          ) : localWidget.widget_type !== 'options_chart' ? (
            <div className="flex items-center gap-2">
              <CategoryAccordion
                categories={mergedCategories}
                selectedItems={editedIndicators}
                onToggle={(indicator) => {
                  // Check if this is a custom indicator
                  const isCustom = indicator.startsWith('custom_');
                  let baseIndicator = indicator;

                  if (isCustom) {
                    // Extract base indicator from custom indicator
                    const customId = parseInt(indicator.replace('custom_', ''));
                    const customIndicator = customIndicators.find(ci => ci.id === customId);
                    if (customIndicator) {
                      baseIndicator = customIndicator.base_indicator;
                    }
                  }

                  toggleIndicator(baseIndicator);
                }}
                displayMode="inline"
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSaveIndicators();
                }}
                onMouseDown={(e) => e.stopPropagation()}
                className="text-xs bg-green-700 hover:bg-green-800 text-white px-2 py-1 rounded transition"
              >
                ✓ Save
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditMode(false);
                  setEditedIndicators(localWidget?.config?.indicators || []);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                className="text-xs bg-gray-600 hover:bg-gray-700 text-white px-2 py-1 rounded transition"
              >
                Cancel
              </button>
            </div>
          ) : null}
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleDelete();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className="text-gray-400 hover:text-red-500 transition"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 p-2 overflow-hidden">
        {localWidget.widget_type === 'price_chart' && (
          <SafeWidgetWrapper
            widget={localWidget}
            onDelete={handleDelete}
            isLinked={isLinked}
            crosshairDate={crosshairDate}
            onCrosshairMove={onCrosshairMove}
            onEdit={handleEditIndicators}
            onUpdateWidget={handleUpdateWidget}
            showBollinger={showBollinger}
            setShowBollinger={setShowBollinger}
          />
        )}
        {localWidget.widget_type === 'options_chart' && (
          <OptionsChartWidget
            widget={localWidget}
            isLinked={isLinked}
            sharedCrosshair={crosshairDate}
            onCrosshairMove={onCrosshairMove}
          />
        )}
      </div>

      {/* Indicator Settings Modal */}
      {showSettingsModal && selectedIndicator && (
        <IndicatorSettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          onSave={handleSaveSettings}
          indicator={selectedIndicator}
          currentSettings={localWidget.config?.indicator_settings?.[selectedIndicator] || {}}
        />
      )}
    </div>
  );
}

// Memoize WidgetCard to prevent unnecessary re-renders
// Only re-render when widget data or linking state changes
export default memo(WidgetCard, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render)
  return (
    prevProps.widget.id === nextProps.widget.id &&
    prevProps.isLinked === nextProps.isLinked &&
    prevProps.crosshairDate === nextProps.crosshairDate &&
    JSON.stringify(prevProps.widget.config) === JSON.stringify(nextProps.widget.config)
  );
});